/**
 * RLQ — Lobby Queue surface (FSR / Supervisor view).
 *
 * Replaces the legacy table layout with a card grid (`rop-card-grid`) so the
 * surface reflows from 1→4 columns based on the app width — same standard the
 * cart cards use.
 *
 * Audience: any provisioned staff (FSR+) in the Lobby tab.
 *
 * Data plumbing:
 *   - Real backend: `client.retail.sessions.list({status:'active'})` filtered to
 *     `sessionType==='lobby' || origin==='kiosk'`.
 *   - Mock provider: `?lobbyMock=1` or `VITE_FEATURE_LOBBY_MOCK=true`.
 *
 * Interaction:
 *   - Click an unclaimed card → claim flow (`/rlq/claim/:id?...`).
 *   - Click a claimed card → no-op (intentional: prevents stealing).
 *   - "My queue" / "Unassigned" / "All" filter chips at the top.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { LayoutGrid, RefreshCw, Users } from 'lucide-react';

import { Button, FilterChips, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { SessionDto } from '@retail-ops/shared-types';

import {
  buildBranchMap,
  buildServiceMap,
  canSeeManagement,
  isKioskRole,
  isLobbyV9Enabled,
  lobbyTicketToVisitor,
  sessionToVisitor,
  sortLobbyVisitors,
  type LobbyVisitor,
} from '../shared/lobbyData';
import { buildMockLobbySessions, isLobbyMockEnabled } from '../shared/lobbyMock';
import { FALLBACK_SERVICE_TYPES, type RequiredPermission, type ServiceTypeTargetModule } from '../types';
import { LobbyQueueCard } from './LobbyQueueCard';

type QueueFilter = 'mine' | 'unassigned' | 'all';

export default function LobbyQueue(): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { config, state: configState } = useConfig();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<ReadonlyArray<SessionDto>>([]);
  // ── WAVE-9 — optional v9 LobbyTicket source (set when feature flag on) ──
  const [v9Visitors, setV9Visitors] = useState<ReadonlyArray<LobbyVisitor> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<QueueFilter>('all');
  const [refreshTick, setRefreshTick] = useState<number>(0);

  // Lookup maps -------------------------------------------------------------
  const serviceMap = useMemo(() => buildServiceMap(FALLBACK_SERVICE_TYPES), []);
  const branchMap = useMemo(() => buildBranchMap(config.branches), [config.branches]);

  // Fetch -------------------------------------------------------------------
  useEffect(() => {
    if (isLobbyMockEnabled()) {
      setSessions(buildMockLobbySessions());
      setLoading(false);
      setError(null);
      return;
    }

    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    // ── WAVE-9 — prefer /v3/lobby/queue when available ────────────────────
    if (isLobbyV9Enabled()) {
      client.retail.lobby
        .queue({ top: 100 }, { signal: ctrl.signal })
        .then((res) => {
          if (ctrl.signal.aborted) return;
          const tickets = res.tickets ?? [];
          let walkInIndex = 0;
          const visitors = tickets.map((t) => {
            walkInIndex += 1;
            return lobbyTicketToVisitor(t, { serviceMap, branchMap, walkInIndex });
          });
          setV9Visitors(visitors);
          setSessions([]);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (ctrl.signal.aborted) return;
          // Fall back to legacy session route on failure (back-compat).
          // eslint-disable-next-line no-console
          console.warn('[WAVE-9] /v3/lobby/queue failed; falling back to /v3/sessions', err);
          loadFromSessions(ctrl);
        });
      return () => ctrl.abort();
    }

    loadFromSessions(ctrl);
    return () => ctrl.abort();

    function loadFromSessions(c: AbortController): void {
      client.retail.sessions
        .list({ status: 'active', top: 100 }, { signal: c.signal })
        .then((res) => {
          if (c.signal.aborted) return;
          const lobby = (res.sessions ?? []).filter(
            (s) => s.sessionType === 'lobby' || s.origin === 'kiosk',
          );
          setV9Visitors(null);
          setSessions(lobby);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (c.signal.aborted) return;
          setSessions([]);
          setV9Visitors(null);
          setError(err instanceof Error ? err.message : 'Unable to load the lobby.');
          setLoading(false);
        });
    }
  }, [client, refreshTick, serviceMap, branchMap]);

  // Adapt + filter + sort ---------------------------------------------------
  const visitors = useMemo<ReadonlyArray<LobbyVisitor>>(() => {
    let all: ReadonlyArray<LobbyVisitor>;
    if (v9Visitors !== null) {
      all = v9Visitors;
    } else {
      let walkInIndex = 0;
      all = sessions.map((s) => {
        walkInIndex += 1;
        return sessionToVisitor(s, { serviceMap, branchMap, walkInIndex });
      });
    }
    const me = user?.upn?.toLowerCase();
    const meName = user?.displayName?.toLowerCase();
    const filtered = all.filter((v) => {
      if (filter === 'mine') {
        if (!v.assignedOfficerName) return false;
        const officer = v.assignedOfficerName.toLowerCase();
        return (me && officer.includes(me)) || (meName && officer.includes(meName));
      }
      if (filter === 'unassigned') return !v.assignedOfficerName;
      return true;
    });
    return sortLobbyVisitors(filtered);
  }, [sessions, v9Visitors, serviceMap, branchMap, user, filter]);

  // Counts (rendered before filter) ----------------------------------------
  const counts = useMemo(() => {
    if (v9Visitors !== null) {
      let unassigned = 0;
      let breached = 0;
      for (const v of v9Visitors) {
        if (!v.assignedOfficerName) unassigned += 1;
        if (v.waitTier === 'breached') breached += 1;
      }
      return { allCount: v9Visitors.length, unassigned, breached };
    }
    const allCount = sessions.length;
    let unassigned = 0;
    let breached = 0;
    for (const s of sessions) {
      const v = sessionToVisitor(s, { serviceMap, branchMap });
      if (!v.assignedOfficerName) unassigned += 1;
      if (v.waitTier === 'breached') breached += 1;
    }
    return { allCount, unassigned, breached };
  }, [sessions, v9Visitors, serviceMap, branchMap]);

  // Permission gate ---------------------------------------------------------
  // TODO(rlq-permissions): When ao_UserRoles.ProductPermissions surfaces in
  // AuthContext, replace this permissive check with a permission-set lookup.
  const permitted = useMemo<Set<RequiredPermission>>(() => {
    if (!user) return new Set(['Any']);
    if (isKioskRole(user.primaryRole, user.isKiosk)) {
      return new Set(['Any']);
    }
    return new Set([
      'Any',
      'NewAccount',
      'AccountMaintenance',
      'CifMaintenance',
      'IRA',
      'CD',
      'SDB',
      'Signing',
      'Compliance',
    ]);
  }, [user]);

  const canClaim = (v: LobbyVisitor): boolean =>
    permitted.has(v.requiredPermission ?? 'Any');

  const claimDisabledReason = (v: LobbyVisitor): string | undefined =>
    canClaim(v) ? undefined : `Requires ${v.requiredPermission} permission.`;

  const handleClaim = (v: LobbyVisitor): void => {
    const target: ServiceTypeTargetModule = v.targetModule ?? 'RCC';
    const qp: string[] = [`target=${target}`];
    if (v._cifId) qp.push(`cifId=${encodeURIComponent(v._cifId)}`);
    if (v._cartId) qp.push(`cartId=${encodeURIComponent(v._cartId)}`);
    navigate(`/rlq/claim/${encodeURIComponent(v._sessionId)}?${qp.join('&')}`);
  };

  const showsManagementShortcut = canSeeManagement(user?.primaryRole);

  // -------------------------------------------------------------------------
  return (
    <div className="lobby-page">
      <header className="lobby-page__header">
        <div className="lobby-page__title-row">
          <h1 className="lobby-page__title">Lobby</h1>
          <Pill variant="muted">Queue</Pill>
          {configState === 'initializing' && (
            <Pill variant="warn">System initializing</Pill>
          )}
          {error && <Pill variant="err">Load error</Pill>}
          {counts.breached > 0 && (
            <Pill variant="err">
              {counts.breached} over SLA
            </Pill>
          )}
        </div>
        <p className="lobby-page__subtitle">
          Visitors waiting to be helped. Click a card to claim and route into the right module.
        </p>
      </header>

      <div className="lobby-toolbar">
        <FilterChips
          label="Show"
          options={[
            { value: 'all', label: `Everyone (${counts.allCount})` },
            { value: 'unassigned', label: `Unassigned (${counts.unassigned})` },
            { value: 'mine', label: 'Assigned to me' },
          ]}
          selected={filter}
          onChange={(v) => setFilter(v as QueueFilter)}
        />
        <Button
          variant="ghost"
          size="sm"
          startIcon={<RefreshCw size={12} />}
          onClick={() => setRefreshTick((n) => n + 1)}
        >
          Refresh
        </Button>
        <Button
          variant="ghost"
          size="sm"
          startIcon={<Users size={12} />}
          onClick={() => navigate('/rlq/entry/internal')}
        >
          Add to lobby
        </Button>
        {showsManagementShortcut && (
          <Button
            variant="primary"
            size="sm"
            startIcon={<LayoutGrid size={12} />}
            onClick={() => navigate('/rlq/management')}
          >
            Management view
          </Button>
        )}
        <span className="lobby-toolbar__summary">
          {loading ? 'Loading…' : `${visitors.length} shown`}
        </span>
      </div>

      {loading ? (
        <div className="lobby-empty">Loading the lobby…</div>
      ) : error ? (
        <div className="lobby-empty">
          <div style={{ color: 'var(--pcb-red)' }}>{error}</div>
          <div style={{ marginTop: 'var(--sp-5)' }}>
            <Button variant="ghost" size="sm" onClick={() => setRefreshTick((n) => n + 1)}>
              Retry
            </Button>
          </div>
        </div>
      ) : visitors.length === 0 ? (
        <div className="lobby-empty lobby-empty--art">
          <div className="lobby-empty__art" aria-hidden>
            <Users size={28} />
          </div>
          <div className="lobby-empty__title">No one is waiting</div>
          <div className="lobby-empty__hint">
            Walk-ins and kiosk check-ins will land here automatically.
          </div>
        </div>
      ) : (
        <div className="rop-card-grid">
          {visitors.map((v) => (
            <LobbyQueueCard
              key={v._sessionId}
              visitor={v}
              canClaim={canClaim(v)}
              {...(claimDisabledReason(v) ? { claimDisabledReason: claimDisabledReason(v)! } : {})}
              onClaim={handleClaim}
            />
          ))}
        </div>
      )}
    </div>
  );
}
